import { useEffect, useMemo, useRef, useState } from "react";
import { Field, Fieldset } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import FormError from "../../Form/FormError";
import Button from "../../Button";
import SlideOver from "../../Modal/SlideOver";
import {
  addLocalNotification,
  useAuthState,
  useNotificationDispatch,
  useAccountingDispatch,
  useAccountingState,
  requestUpdateAcctTransaction,
  requestListDebitAccount,
  clearAccountingTransaction,
  clearAcctTransactionError,
  requestListCreditAccount,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
  requestListBusinessPartner,
  requestCreateAcctTransaction,
  requestListAccount,
  usePeriodLock,
} from "../../../../Context";
import {
  cancelAPIRequest,
  getPeriodClosedError,
  resolvePickedDateTime,
} from "../../../../Utils";
import { resolveTransactionType } from "../../../../Utils/AccountingUtil";
import PeriodClosedNotice from "../PeriodClosedNotice";
import FormDate from "../../Form/FormDate";
import FormInput from "../../Form/FormInput";
import {
  partnerTransactionTypes,
  PAYMENT_TYPES,
  TRANSACTION_TYPES,
} from "../../../../Constants";
import FormSelect from "../../Form/FormSelect";
import FormRadioGroup from "../../Form/FormRadioGroup";
import JournalEntryTable from "../../Table/JournalEntryTable";
import InitAccountPromptModal from "../InitAccountPromptModal";
import { isBusinessWriteBlocked } from "../../../../Constants/business";
import WriteBlockGate from "../../Business/WriteBlockGate";

const TransactionModal = ({
  open,
  onClose,
  transaction,
  transactionType,
  cashBasisOnly,
}) => {
  const { t, i18n } = useTranslation();

  const [date, setDate] = useState("");
  const [type, setType] = useState(null);
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [paid, setPaid] = useState(PAYMENT_TYPES[0]);
  const [debitAccount, setDebitAccount] = useState(null);
  const [creditAccount, setCreditAccount] = useState(null);
  const [dueDate, setdueDate] = useState(null);
  const [selectedPartner, setselectedPartner] = useState(null);
  const [errors, setErrors] = useState({});

  const [accountItems, setaccountItems] = useState([
    {
      account: "",
      debit: { value: "0", floatValue: 0 },
      credit: { value: "0", floatValue: 0 },
    },
  ]);
  const [inputErrors, setinputErrors] = useState({});
  const [accountItemsError, setaccountItemsError] = useState("");
  const isFormSubmit = useRef(false);

  const dispatch = useAccountingDispatch();
  const notificationDispatch = useNotificationDispatch();
  const businessDispatch = useBusinessTransactionDispatch();

  const { user, getUserLoading: userLoading } = useAuthState();
  const writeBlocked = isBusinessWriteBlocked(user?.mainBusiness);

  const {
    minDate,
    isDateClosed,
    isClosed,
    closedThroughLabel,
    refetch: refetchPeriod,
  } = usePeriodLock(user?.mainBusiness?.id, { locale: i18n.language });

  // Two different situations, both blocking. `minDate` stops a *new* journal
  // being dated into the closed period; this catches an *existing* one that is
  // already there — reached by editing a locked row from a surface that has not
  // swapped its actions cell yet, or by a book closed after this modal opened.
  const dateLocked = isDateClosed(date);
  const {
    request,
    creditAccountRequest,
    debitAccountRequest,
    debits,
    debitLoading,
    debitError,
    credits,
    creditLoading,
    creditError,
    transactionID,
    submitLoading,
    submitError,
    accounts,
    accountLoading,
    accountError,
    allAccountLoading,
  } = useAccountingState();
  const { businessPartners, businessPartnerLoading, businessPartnerError } =
    useBusinessTransactionState();

  const isEdit = Boolean(transaction);

  const incomeType = transactionType === "INCOME";
  const expenseType = transactionType === "EXPENSE";

  const updateAccountItem = (index, updatedProperties) => {
    return (prevState) => {
      const updatedItems = [...prevState];

      updatedItems[index] = {
        ...updatedItems[index],
        ...updatedProperties,
      };

      return updatedItems;
    };
  };

  const handleRemoveRow = (index) => {
    const newAccountItems = [...accountItems];
    newAccountItems.splice(index, 1);
    setaccountItems(newAccountItems);
  };

  const handleAddRow = () => {
    if (accountItems.length < 100) {
      setaccountItems([
        ...accountItems,
        {
          account: "",
          debit: { value: "0", floatValue: 0 },
          credit: { value: "0", floatValue: 0 },
        },
      ]);
    }
  };

  // Filter and transform transaction types for specific use cases
  const transactionTypes = useMemo(() => {
    let relevantTypes;

    if (cashBasisOnly) {
      // Cash basis only: restrict to INCOME and EXPENSE
      relevantTypes = incomeType
        ? ["INCOME"]
        : expenseType
          ? ["EXPENSE"]
          : ["INCOME", "EXPENSE"];
    } else {
      relevantTypes = incomeType
        ? ["INCOME", "INCOME_RECEIVABLE"]
        : expenseType
          ? ["EXPENSE", "EXPENSE_PAYABLE"]
          : null;
    }

    const selectableTransactionTypes = TRANSACTION_TYPES.filter(
      (item) => !item.systemGenerated,
    );

    return (
      relevantTypes
        ? selectableTransactionTypes.filter((item) =>
            relevantTypes.includes(item.value),
          )
        : selectableTransactionTypes
    ).map((item) => ({
      ...item,
      label: t(item.label),
    }));
  }, [t, expenseType, incomeType, cashBasisOnly]);

  const sortAccounts = (accounts) => {
    return accounts.slice().sort((a, b) => {
      const aCode = parseInt(a.code, 10);
      const bCode = parseInt(b.code, 10);
      return (aCode < 400000) - (bCode < 400000) || aCode - bCode;
    });
  };

  // Generate options for debits/credits
  const options = useMemo(() => {
    const sortAndMap = (accounts) =>
      sortAccounts(accounts).map(({ id, code, name }) => ({
        value: id,
        label: `${code} - ${name}`,
      }));

    return {
      debitOptions: sortAndMap(debits),
      creditOptions: sortAndMap(credits),
    };
  }, [debits, credits]);

  //Generate options for parners
  const partnerOptions = useMemo(
    () =>
      businessPartners.map((item) => ({
        ...item,
        value: item.id,
        label: item.name,
      })),
    [businessPartners],
  );

  // Force paid status to true when cashBasisOnly
  useEffect(() => {
    if (cashBasisOnly) {
      setPaid(PAYMENT_TYPES[0]);
    }
  }, [cashBasisOnly]);

  // Clear errors on unmount
  useEffect(() => {
    return () => {
      if (request) {
        cancelAPIRequest(request);
      }
      clearAccountingTransaction(dispatch);
      clearAcctTransactionError(dispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Init-account guard: a business without a chart of accounts cannot post,
  // so offer the skippable init flow before showing the create form.
  const [initSkipped, setInitSkipped] = useState(false);
  // Latched probe result, kept locally on purpose: `accounts` is shared state
  // that other effects reset to [] mid-fetch (picking type OTHER re-fetches
  // with loadAll, and REQUEST_LIST_ALL_ACCOUNT leaves accountLoading false),
  // so deriving "no chart of accounts" straight from it makes the prompt flash.
  // null = not decided yet.
  const [chartEmpty, setChartEmpty] = useState(null);
  // Set once a list request has actually been observed in flight, so the very
  // first render (accounts still [], nothing loading) cannot latch empty.
  const listSeenLoading = useRef(false);

  useEffect(() => {
    if (open && !isEdit && user?.mainBusiness?.id) {
      listSeenLoading.current = false;
      setChartEmpty(null);
      requestListAccount(dispatch, {
        businessID: user.mainBusiness.id,
        limit: 1,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, user?.mainBusiness?.id]);

  useEffect(() => {
    if (!open || isEdit) return;
    if (accountLoading || allAccountLoading) {
      listSeenLoading.current = true;
      return;
    }
    if (!listSeenLoading.current || accountError) return;
    // Once a chart has been seen it is never unseen — only the first settled
    // empty result may raise the prompt.
    if (accounts.length > 0) setChartEmpty(false);
    else setChartEmpty((prev) => (prev === null ? true : prev));
  }, [
    open,
    isEdit,
    accountLoading,
    allAccountLoading,
    accountError,
    accounts.length,
  ]);

  const showInitPrompt = open && !isEdit && !initSkipped && chartEmpty === true;

  useEffect(() => {
    if (!transaction && transactionType) {
      setType(transactionTypes.find((type) => type.value === transactionType));
    }
  }, [transaction, transactionType, transactionTypes]);

  // Populate form when editing
  useEffect(() => {
    if (transaction) {
      setType(transactionTypes.find((type) => type.value === transaction.type));
      setDate(new Date(transaction.createdAt) || "");
      setDescription(transaction.description || "");
      setAmount(transaction.total?.toString() || "");
      // Guard the null, do not lean on `||`: `new Date(null)` is the epoch and
      // the epoch is truthy, so the fallback never ran and a transaction with
      // no due date opened showing 01/01/1970 — which `payment_due_at` then
      // wrote back on save.
      setdueDate(
        transaction?.paymentDueAt ? new Date(transaction.paymentDueAt) : null,
      );
      setselectedPartner(
        partnerOptions.find(
          (partner) => partner.value === transaction?.partnerID,
        ) || null,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction]);

  //  Handle update success
  useEffect(() => {
    if (transactionID && isEdit) {
      // Close modal
      onClose(true);
      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `edit-transaction-${transactionID}`,
        type: "success",
        title: "common.success",
        body: "transactions.notifications.updateSuccess",
        showFull: true,
      });
    }
  }, [notificationDispatch, transactionID, onClose, isEdit]);

  //  Handle create success
  useEffect(() => {
    if (transactionID && !isEdit) {
      // Close modal
      onClose(true);
      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `create-transaction-${transactionID}`,
        type: "success",
        title: "common.success",
        body: "transactions.notifications.createSuccess",
        showFull: true,
      });
    }
  }, [notificationDispatch, transactionID, onClose, isEdit]);

  // Reload debit accounts if paid status changed
  useEffect(() => {
    if (debitAccountRequest) {
      cancelAPIRequest(debitAccountRequest);
    }
    if (!paid?.value) {
      if (type?.debitUnpaidAccounts && user.mainBusiness.id) {
        requestListDebitAccount(dispatch, {
          businessID: user.mainBusiness.id,
          categories: type.debitUnpaidAccounts,
        });
      }
    } else {
      if (type?.debitAccounts && user.mainBusiness.id) {
        requestListDebitAccount(dispatch, {
          businessID: user.mainBusiness.id,
          categories: type.debitAccounts,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    type?.debitAccounts,
    type?.debitUnpaidAccounts,
    paid?.value,
    user.mainBusiness?.id,
  ]);

  // Reload credit accounts if paid status changed
  useEffect(() => {
    if (creditAccountRequest) {
      cancelAPIRequest(creditAccountRequest);
    }
    if (!paid?.value) {
      if (type?.creditUnpaidAccounts && user.mainBusiness.id) {
        requestListCreditAccount(dispatch, {
          businessID: user.mainBusiness.id,
          categories: type.creditUnpaidAccounts,
        });
      }
    } else {
      if (type?.creditAccounts && user.mainBusiness.id) {
        requestListCreditAccount(dispatch, {
          businessID: user.mainBusiness.id,
          categories: type.creditAccounts,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    type?.creditAccounts,
    type?.creditUnpaidAccounts,
    paid?.value,
    user.mainBusiness?.id,
  ]);

  // The account pickers are already filtered by paid status; the labels have to
  // follow, or an unpaid sale offers only piutang while still reading "Simpan
  // ke". Types whose accounts do not change with paid status define no unpaid
  // label and keep the one they have.
  const accountLabels = useMemo(() => {
    const unpaid = !paid?.value;
    return {
      debit:
        (unpaid ? type?.debitUnpaidLabel : null) ||
        type?.debitLabel ||
        "transactions.fields.debitAccount",
      credit:
        (unpaid ? type?.creditUnpaidLabel : null) ||
        type?.creditLabel ||
        "transactions.fields.creditAccount",
    };
  }, [
    paid?.value,
    type?.debitLabel,
    type?.debitUnpaidLabel,
    type?.creditLabel,
    type?.creditUnpaidLabel,
  ]);

  // Populate form when editing on debit or credit account
  useEffect(() => {
    if (transaction) {
      const { items } = transaction.accounts || {};

      if (transaction.type === "OTHER") {
        if (items) {
          const mappedItems = items.map((item) => {
            let accountLabel = item.account?.name;
            const selectedAccount = accounts.find(
              (account) => account.id === item.accountID,
            );

            if (selectedAccount) {
              accountLabel = `${selectedAccount?.code} - ${selectedAccount?.name}`;
            }
            return {
              account: {
                label: accountLabel,
                value: item.account?.id,
              },
              debit: { value: `${item.debit}`, floatValue: item.debit },
              credit: { value: `${item.credit}`, floatValue: item.credit },
            };
          });
          setaccountItems(mappedItems);
        }
      } else {
        if (items) {
          items.forEach((account) => {
            if (account.debit > 0) {
              const { account: debitAccount } = account;
              setDebitAccount({
                ...debitAccount,
                label: debitAccount.name,
                value: debitAccount.id,
              });
            }
            if (account.credit > 0) {
              const { account: creditAccount } = account;
              setCreditAccount({
                ...creditAccount,
                label: creditAccount.name,
                value: creditAccount.id,
              });
            }
          });
        }
      }
    }
  }, [accounts, transaction]);

  useEffect(() => {
    if (user?.mainBusiness?.id) {
      if (partnerTransactionTypes.includes(type?.value)) {
        requestListBusinessPartner(businessDispatch, {
          businessID: user?.mainBusiness?.id,
        });
      }
    }
  }, [user?.mainBusiness?.id, type?.value, businessDispatch]);

  useEffect(() => {
    if (type?.value === "OTHER" && user?.mainBusiness?.id) {
      requestListAccount(dispatch, {
        businessID: user.mainBusiness.id,
        loadAll: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.mainBusiness?.id, type?.value]);

  // Almost every field here is a dropdown, and picking from one fires no
  // input event — SlideOver's fallback detection would miss a fully filled
  // journal. The type field is excluded: the caller preselects it.
  const dirty =
    isEdit ||
    Boolean(
      date ||
      description ||
      amount ||
      debitAccount ||
      creditAccount ||
      selectedPartner ||
      dueDate,
    ) ||
    accountItems.some(
      (item) =>
        item.account || item.debit?.floatValue || item.credit?.floatValue,
    );

  const clearError = (field) =>
    setErrors((prev) => ({ ...prev, [field]: undefined }));

  const handleFieldChange = (setter, field) => (eventOrValue) => {
    const value =
      eventOrValue?.target !== undefined
        ? eventOrValue.target.value
        : eventOrValue;
    setter(value);
    clearError(field);
  };

  // The pickers refetch whenever the type or the paid status changes, but a
  // selection made against the old list outlives the swap: an unpaid purchase
  // switched to paid kept Hutang Usaha in a field that now offers cash and
  // bank only. Cleared here rather than in the reload effects because those
  // also fire when the edit form populates its type, and the account prefill
  // lands after them — clearing there would wipe it.
  const clearAccountSelection = () => {
    setDebitAccount(null);
    setCreditAccount(null);
    setErrors((prev) => ({
      ...prev,
      debitAccount: undefined,
      creditAccount: undefined,
    }));
  };

  const handleTypeChange = (value) => {
    clearAccountSelection();
    handleFieldChange(setType, "type")(value);
  };

  const handlePaidChange = (value) => {
    clearAccountSelection();
    setPaid(value);
  };

  // A bare day click carries no time, so the first pick is given the current
  // clock — without it every journal entered today serialises to one instant and
  // the ledger cannot be ordered. resolvePickedDateTime holds the exact rule.
  const handleDateChange = (value) => {
    setDate(resolvePickedDateTime(value, date));
    clearError("date");
  };

  const validate = () => {
    const newErrors = {};

    if (!date) {
      newErrors.date = "transactions.errors.dateRequired";
    }

    if (!type?.value) {
      newErrors.type = "transactions.errors.typeRequired";
    }

    if (!["OTHER"].includes(type?.value)) {
      if (!amount) {
        newErrors.amount = "transactions.errors.amountRequired";
      }

      if (!debitAccount?.value) {
        newErrors.debitAccount = "transactions.errors.debitAccount";
      }

      if (!creditAccount?.value) {
        newErrors.creditAccount = "transactions.errors.creditAccount";
      }
    }

    if (["OTHER"].includes(type?.value)) {
      const validationErrors = {};
      const validateRequiredField = (
        fieldName,
        fieldValue,
        displayFieldName,
      ) => {
        if (!fieldValue) {
          validationErrors[fieldName] = `${displayFieldName} is required.`;
        }
      };

      const validateAccount = accountItems.every((entry) => {
        return entry.account.value;
      });
      const validateDebitAmount = accountItems.every((entry) => {
        return entry.debit.floatValue !== undefined;
      });
      const validateCreditAmount = accountItems.every((entry) => {
        return entry.credit.floatValue !== undefined;
      });

      validateRequiredField("accountItems", validateAccount, "Account");
      validateRequiredField("debitAmount", validateDebitAmount, "Debit amount");
      validateRequiredField(
        "creditAmount",
        validateCreditAmount,
        "Credit amount",
      );

      for (const entry of accountItems) {
        const debit = entry.debit.floatValue;
        const credit = entry.credit.floatValue;

        if (!((debit !== 0 && credit === 0) || (debit === 0 && credit !== 0))) {
          validationErrors["debitCredit"] =
            "transactions.errors.debitCreditInvalid";
          setinputErrors(validationErrors);
          return;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    isFormSubmit.current = true;

    if (!validate()) return;
    // The submit button is already disabled here; this guards the form's own
    // Enter-key submit path, which never consults the button.
    if (dateLocked) return;

    saveExpense();
  };

  const saveExpense = () => {
    const input = {
      transaction_id: isEdit ? transaction.id : undefined,
      business_id: user.mainBusiness.id,
      accounts: [],
      // `paid` is deliberately absent: the API derives it from the type and
      // never reads the field. resolveTransactionType holds the rule.
      type: resolveTransactionType(type.value, paid?.value),
      created_at: date.toISOString(),
      description,
      total: Number(amount),
      payment_due_at: dueDate?.toISOString(),
    };

    if (selectedPartner?.value) {
      input.partner_id = selectedPartner.value;
    } else {
      input.partner_id = null;
    }

    if (creditAccount?.value) {
      input.accounts.push({
        id: creditAccount.value,
        debit: 0,
        credit: input.total,
      });
    }

    if (debitAccount?.value) {
      input.accounts.push({
        id: debitAccount.value,
        debit: input.total,
        credit: 0,
      });
    }

    if (["OTHER"].includes(type?.value)) {
      if (accountItems.length < 2) {
        return;
      }

      input.accounts = accountItems.map(({ account, debit, credit }) => ({
        id: account.value,
        debit: debit.floatValue,
        credit: credit.floatValue,
      }));

      const encounteredIds = new Set();
      let duplicateFound = false;
      input.accounts.forEach(({ id }) => {
        if (encounteredIds.has(id)) {
          setaccountItemsError("transactions.errors.duplicateAccount");
          duplicateFound = true;
          return;
        }
        encounteredIds.add(id);
      });

      if (duplicateFound) return;

      const totalDebit = input.accounts.reduce(
        (acc, { debit }) => acc + debit,
        0,
      );
      const totalCredit = input.accounts.reduce(
        (acc, { credit }) => acc + credit,
        0,
      );

      if (totalDebit !== totalCredit) {
        setaccountItemsError("transactions.errors.debitCreditUnbalance");
        return;
      } else {
        setaccountItemsError("");
      }
      input.total = totalDebit || totalCredit;
    }

    if (isEdit) {
      requestUpdateAcctTransaction(dispatch, { input });
    } else {
      requestCreateAcctTransaction(dispatch, { input });
    }
  };

  return (
    <>
      <SlideOver
        open={open}
        onClose={onClose}
        dirty={dirty}
        title={
          isEdit
            ? "transactions.modal.title.edit"
            : "transactions.modal.title.add"
        }
        footer={
          <div className="flex justify-end space-x-3">
            <Button type="button" variant="secondary" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button
              disabled={
                writeBlocked ||
                dateLocked ||
                submitLoading ||
                allAccountLoading ||
                creditLoading ||
                debitLoading
              }
              loading={submitLoading}
              type="submit"
              form="new-transaction-form"
              variant="primary"
            >
              {isEdit ? t("common.save") : t("common.create")}
            </Button>
          </div>
        }
      >
        <form
          id="new-transaction-form"
          className="space-y-4 mb-6"
          onSubmit={handleSubmit}
        >
          <WriteBlockGate>
            <PeriodClosedNotice error={submitError} onRefresh={refetchPeriod} />
            {/* A period rejection is a status, not a failure, so it must not also
            appear in the red error box — one event, one message. */}
            <FormError
              error={getPeriodClosedError(submitError) ? null : submitError}
            />
            <Fieldset
              disabled={userLoading || submitLoading}
              className="space-y-4 data-[disabled]:opacity-50"
            >
              <Field>
                <FormSelect
                  label="transactions.fields.type"
                  id="type"
                  name="type"
                  required
                  options={transactionTypes}
                  value={type}
                  onChange={handleTypeChange}
                  placeholder={t("transactions.placeholders.type")}
                  error={errors.type}
                />
              </Field>

              {["INCOME", "EXPENSE"].includes(type?.value) &&
                !cashBasisOnly && (
                  <FormRadioGroup
                    required
                    label="transactions.fields.paymentStatus"
                    name="type"
                    options={PAYMENT_TYPES}
                    value={paid}
                    onChange={handlePaidChange}
                    gridClassName="grid-cols-1 sm:grid-cols-2"
                    error={errors.productType}
                  />
                )}

              <Field>
                {/* Prevention lives here: closed days are simply not offered, so the
                409 stays a safety net for data races rather than the main path. */}
                <FormDate
                  required
                  label="transactions.fields.date"
                  name="date"
                  selected={date}
                  onChange={handleDateChange}
                  dateFormat="dd/MM/yyyy HH:mm"
                  showTimeInput
                  timeInputLabel={t("common.time")}
                  minDate={minDate}
                  isDateClosed={isDateClosed}
                  placeholderText={t("transactions.placeholders.date")}
                  error={errors.date}
                />
                {/* Says why days are greyed out. Without it the calendar just looks
                broken — the constraint has to be legible, not only enforced. */}
                {isClosed && (
                  <p className="mt-1.5 text-xs text-amber-700">
                    {dateLocked
                      ? t("accounting.period.hints.dateLocked", {
                          date: closedThroughLabel,
                        })
                      : t("accounting.period.banner.closedThrough", {
                          date: closedThroughLabel,
                        })}
                  </p>
                )}
              </Field>

              <Field>
                <FormInput
                  hint="common.optional"
                  label="transactions.fields.description"
                  id="description"
                  name="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  onClear={() => setDescription("")}
                  placeholder={t("transactions.placeholders.description")}
                />
              </Field>

              {type?.value === "OTHER" ? (
                <Field>
                  <JournalEntryTable
                    accountItems={accountItems}
                    setaccountItems={setaccountItems}
                    accounts={accounts}
                    debitError={debitError}
                    allAccountLoading={allAccountLoading}
                    inputErrors={inputErrors}
                    isFormSubmit={isFormSubmit}
                    updateAccountItem={updateAccountItem}
                    handleRemoveRow={handleRemoveRow}
                    handleAddRow={handleAddRow}
                    selectedBusiness={user.mainBusiness}
                    accountingDispatch={dispatch}
                    requestListAccount={requestListAccount}
                    accountItemsError={accountItemsError}
                  />
                </Field>
              ) : (
                <>
                  <Field>
                    <FormInput
                      required
                      label="transactions.fields.amount"
                      id="amount"
                      name="amount"
                      value={amount}
                      onClear={() => setAmount("")}
                      placeholder={t("transactions.placeholders.amount")}
                      // `amount` holds the plain number ("3000000.5"), not the
                      // text on screen: fed back as "3.000", the "." was read
                      // as a decimal point and the field turned it into ",".
                      numberFormat={{
                        thousandSeparator: ".",
                        decimalSeparator: ",",
                        // A "." typed out of habit for thousands is ignored,
                        // not taken as the decimal point.
                        allowedDecimalSeparators: [","],
                        valueIsNumericString: true,
                        onValueChange: ({ value }) =>
                          handleFieldChange(setAmount, "amount")(value),
                      }}
                      prefix="Rp"
                      error={errors.amount}
                    />
                  </Field>

                  <Field>
                    <FormSelect
                      required
                      label={accountLabels.debit}
                      id="debitAccount"
                      name="debitAccount"
                      searchable
                      options={options.debitOptions}
                      isLoading={debitLoading}
                      value={debitAccount}
                      onChange={handleFieldChange(
                        setDebitAccount,
                        "debitAccount",
                      )}
                      placeholder={t("transactions.placeholders.debitAccount")}
                      error={errors.debitAccount || debitError}
                    />
                  </Field>

                  <Field>
                    <FormSelect
                      required
                      label={accountLabels.credit}
                      id="creditAccount"
                      name="creditAccount"
                      searchable
                      options={options.creditOptions}
                      isLoading={creditLoading}
                      value={creditAccount}
                      onChange={handleFieldChange(
                        setCreditAccount,
                        "creditAccount",
                      )}
                      placeholder={t("transactions.placeholders.creditAccount")}
                      error={errors.creditAccount || creditError}
                    />
                  </Field>

                  <Field>
                    <FormSelect
                      hint="common.optional"
                      label="transactions.fields.partner"
                      id="partner"
                      name="partner"
                      searchable
                      options={partnerOptions}
                      isLoading={businessPartnerLoading}
                      value={selectedPartner}
                      onChange={setselectedPartner}
                      placeholder={t("transactions.placeholders.partner")}
                      error={errors.partner || businessPartnerError}
                    />
                  </Field>
                </>
              )}

              {!cashBasisOnly &&
              ([
                "INCOME_RECEIVABLE",
                "EXPENSE_PAYABLE",
                "PAYABLE",
                "RECEIVABLE",
              ].includes(type?.value) ||
                !paid.value) ? (
                <Field>
                  <FormDate
                    hint="common.optional"
                    label="transactions.fields.dueDate"
                    name="dueDate"
                    selected={dueDate}
                    onChange={setdueDate}
                    dateFormat="dd/MM/yyyy"
                    placeholderText={t("transactions.placeholders.date")}
                  />
                </Field>
              ) : null}
            </Fieldset>
          </WriteBlockGate>
        </form>
      </SlideOver>
      {showInitPrompt && (
        <InitAccountPromptModal
          open={showInitPrompt}
          businessID={user?.mainBusiness?.id}
          onSkip={() => setInitSkipped(true)}
        />
      )}
    </>
  );
};

export default TransactionModal;
